import { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { Grid } from '@mui/material';
import type { QuickChartData } from '@trinity/chat';
import { Remirror as RemirrorTypes, setInsightsDataUtil } from '@trinity/chat';
import type { RemirrorEventListener } from 'remirror';
import { ColorSelect, defaultColor } from '../ColorSelect';
import Input from '../Input';
import { Header, Modal } from '../Modal';
import ButtonSet from '../Modal/ButtonSet';
import Button from '../presentation/Button';
import type { BasicMarkupEditorExtensions } from '../Remirror/BasicMarkupEditor';
import { BasicMarkupEditor } from '../Remirror/BasicMarkupEditor';

type KPIWidgetSettingsProps = {
  open: boolean;
  onClose: () => void;
  chartData: QuickChartData & { type: 'kpi-widget' };
  onSave: (updatedChartData: QuickChartData & { type: 'kpi-widget' }) => void;
};

type RootNode = RemirrorTypes.RootNode;
const NodeType = RemirrorTypes.NodeType;

export function KPIWidgetSettings({
  open,
  onClose,
  chartData,
  onSave,
}: KPIWidgetSettingsProps) {
  const [title, setTitle] = useState('');
  const [backgroundColor, setBackgroundColor] = useState(
    chartData.backgroundColor || 'var(--gray-xl)',
  );
  const [labels, setLabels] = useState<string[]>(() => {
    const defaultLabels = Array.from({ length: 6 }, (_, i) => `KPI ${i + 1}`);
    return chartData.labels?.slice() || defaultLabels;
  });
  const [values, setValues] = useState<string[]>(() => {
    return chartData.values || Array(6).fill('0');
  });
  const [insightsContent, setInsightsContent] = useState<RootNode | null>(() => {
    return setInsightsDataUtil(chartData);
  });
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      if (chartData.title.length > 60) {
        setTitle(chartData.title.slice(0, 60));
      } else {
        setTitle(chartData.title);
      }
    }
  }, [open, chartData]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTitle(e.target.value);
      setTouched(true);
    },
    [],
  );

  const handleBackgroundColorChange = useCallback((color: string) => {
    setBackgroundColor(color);
    setTouched(true);
  }, []);

  const handleLabelChange = useCallback((index: number, value: string) => {
    setLabels((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    setTouched(true);
  }, []);

  const handleValueChange = useCallback((index: number, value: string) => {
    setValues((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    setTouched(true);
  }, []);

  const handleInsightsChange: RemirrorEventListener<BasicMarkupEditorExtensions> =
    useCallback((params) => {
      if (!params.tr?.docChanged) return;

      const newValue = params.state.doc.toJSON() as RootNode;
      setInsightsContent(newValue);
      setTouched(true);
    }, []);

  const handleSave = useCallback(() => {
    // Convert string values to numbers, filtering out empty/invalid values
    const labelValues = values.map((v) => v?.trim()).filter((v) => !!v);

    // Get corresponding labels for valid values
    const validLabels = labels.slice(0, labelValues.length);

    // Serialize insights
    let insightsString = '';
    if (
      insightsContent &&
      insightsContent.content &&
      insightsContent.content.length > 0
    ) {
      // Only serialize if there's actual content (not just empty paragraphs)
      const hasContent = insightsContent.content.some(
        (node) => node.content && node.content.length > 0,
      );
      if (hasContent) {
        insightsString = JSON.stringify(insightsContent);
      }
    }

    const updatedChartData: QuickChartData & { type: 'kpi-widget' } = {
      ...chartData,
      title: title.trim(),
      backgroundColor,
      values: labelValues,
      labels: validLabels,
      insights: insightsString || undefined,
    };

    onSave(updatedChartData);
    onClose();
  }, [
    chartData,
    title,
    backgroundColor,
    values,
    labels,
    insightsContent,
    onSave,
    onClose,
  ]);

  const handleCancel = useCallback(() => {
    // Reset to original values
    setTitle(chartData.title || 'KPI Dashboard');
    setBackgroundColor(chartData.backgroundColor || defaultColor);
    const defaultLabels = Array.from({ length: 6 }, (_, i) => `KPI ${i + 1}`);
    setLabels(chartData.labels?.slice() || defaultLabels);
    setValues(chartData.values?.map((v) => v.toString()) || Array(6).fill('0'));
    setTouched(false);
    onClose();
  }, [chartData, onClose]);

  // Validation
  const trimmedTitle = title.trim();
  const titleLength = trimmedTitle.length;
  const minLen = 1;
  const maxLen = 60;
  const titleValid = titleLength >= minLen && titleLength <= maxLen;

  // Check if at least one value is valid
  const hasValidValues = values.some((v) => {
    const trimmed = v.trim();
    return trimmed && !isNaN(parseFloat(trimmed));
  });

  const canSave = titleValid && trimmedTitle.length > 0 && hasValidValues;
  const showTitleError = touched && !titleValid;
  const titleErrorMessage = showTitleError
    ? titleLength < minLen
      ? `Title is required.`
      : `Title must be less than or equal to ${maxLen} characters.`
    : '';

  return (
    <Modal open={open} onClose={handleCancel}>
      <Header>Edit Metric Card</Header>

      <ModalSection>
        <ModalLabel>Card Header *</ModalLabel>
        <Input
          name="widget-title"
          type="text"
          value={title}
          onChange={handleTitleChange}
          style={{ width: '100%' }}
          placeholder="Enter widget title"
        />
        <FieldMeta>
          <FieldInfo>
            {titleLength}/{maxLen} characters
          </FieldInfo>
          {titleErrorMessage && <FieldError>{titleErrorMessage}</FieldError>}
        </FieldMeta>
      </ModalSection>

      <ModalSection>
        <ModalLabel>Color</ModalLabel>
        <ColorSelect
          color={backgroundColor}
          onChange={handleBackgroundColorChange}
          zIndex={1350}
        />
      </ModalSection>

      <ModalSection>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <ModalLabel>Label</ModalLabel>
          </Grid>
          <Grid item xs={4}>
            <ModalLabel>Value</ModalLabel>
          </Grid>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid container item key={index} spacing={2}>
              <Grid item xs={8}>
                <Input
                  type="text"
                  value={labels[index] || ''}
                  onChange={(e) => handleLabelChange(index, e.target.value)}
                  placeholder={`KPI ${index + 1} Label`}
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  type="text"
                  value={values[index] || ''}
                  onChange={(e) => handleValueChange(index, e.target.value)}
                  placeholder="Value"
                  style={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          ))}
        </Grid>
      </ModalSection>

      <ModalSection>
        <ModalLabel>Add Insights (Optional)</ModalLabel>
        <InsightsEditor>
          <BasicMarkupEditor
            content={insightsContent}
            onChange={handleInsightsChange}
          />
        </InsightsEditor>
        <FieldHint>
          Add context, analysis, or additional information about this metric
          card
        </FieldHint>
      </ModalSection>

      <ButtonSet>
        <Button.Destructive variant="brick" onClick={handleCancel}>
          Cancel
        </Button.Destructive>
        <Button.Primary
          onClick={handleSave}
          disabled={!canSave}
          variant="brick">
          Save
        </Button.Primary>
      </ButtonSet>
    </Modal>
  );
}

const ModalSection = styled.div({
  marginBottom: '24px',
});

const ModalLabel = styled.div({
  fontSize: 18,
  fontFamily: 'var(--font-semibold)',
  color: 'var(--black)',
});

const FieldMeta = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '4px',
});

const FieldInfo = styled.div({
  fontSize: '12px',
  color: '#666',
});

const FieldError = styled.div({
  fontSize: '12px',
  color: '#d32f2f',
});

const FieldHint = styled.div({
  fontSize: '12px',
  color: '#666',
  marginTop: '8px',
  fontStyle: 'italic',
});

const InsightsEditor = styled.div({
  minHeight: '120px',
  marginTop: '8px',
});
